// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Col, Form, Input, Row, Select, Steps} from "antd";
import * as AuthBackend from "./AuthBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Util from "./Util";
import * as Setting from "../Setting";
import i18next from "i18next";
import {SendCodeInput} from "../common/SendCodeInput";
import * as UserBackend from "../backend/UserBackend";
import CustomGithubCorner from "../common/CustomGithubCorner";
import {withRouter} from "react-router-dom";
import * as PasswordChecker from "../common/PasswordChecker";
import {ArrowLeftOutlined} from "@ant-design/icons";

const {Option} = Select;

class ForgetPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      applicationName: props.applicationName ?? props.match.params?.applicationName,
      msg: null,
      name: props.account ? props.account.name : "",
      username: props.account ? props.account.name : "",
      phone: "",
      email: "",
      dest: "",
      isVerifyTypeFixed: false,
      verifyType: "", // "email", "phone"
      current: 0,
      time: 4,
    };
    this.form = React.createRef();
    this.timer = null;
  }

  componentDidMount() {
    if (this.getApplicationObj() === undefined) {
      if (this.state.applicationName !== undefined) {
        this.getApplication();
      } else {
        Setting.showMessage("error", i18next.t("forget:Unknown forget type") + ": " + this.state.type);
      }
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  getApplication() {
    if (this.state.applicationName === undefined) {
      return;
    }

    ApplicationBackend.getApplication("admin", this.state.applicationName)
      .then((res) => {
        if (res.status === "error") {
          Setting.showMessage("error", res.msg);
          return;
        }
        this.onUpdateApplication(res.data);
      });
  }
  getApplicationObj() {
    return this.props.application;
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  onFormFinish(name, info, forms) {
    switch (name) {
    case "step1":
      {
        const organization = forms.step1.getFieldValue("organization");
        const email = forms.step1.getFieldValue("username");
        AuthBackend.checkEmailExists(organization, email)
          .then((res) => {
            if (res.status === "ok") {
              if (res.data?.exists) {
                this.setState({
                  name: email,
                  phone: "",
                  email: email,
                  verifyType: "email",
                  isVerifyTypeFixed: true,
                  dest: email,
                  current: 1,
                });
              } else {
                Setting.showMessage("error", i18next.t("forget:Email does not exist"));
              }
            } else {
              Setting.showMessage("error", res.msg);
            }
          });
      }
      break;
    case "step2":
      UserBackend.verifyCode({
        application: forms.step2.getFieldValue("application"),
        organization: forms.step2.getFieldValue("organization"),
        username: forms.step2.getFieldValue("dest"),
        name: this.state.name,
        code: forms.step2.getFieldValue("code"),
        type: "login",
      }).then(res => {
        if (res.status === "ok") {
          this.setState({current: 2, code: forms.step2.getFieldValue("code")});
        } else {
          Setting.showMessage("error", res.msg);
        }
      });

      break;
    case "step3":
      const values = forms.step3.getFieldsValue();
      values.username = this.state.name;
      values.userOwner = this.getApplicationObj()?.organizationObj.name;
      UserBackend.setPassword(values.userOwner, values.username, "", values?.newPassword, this.state.code).then(res => {
        if (res.status === "ok") {
          this.setState({current: 3});
          this.timer = setInterval(() => {
            this.setState({time: this.state.time - 1}, () => {
              if (this.state.time === 0) {
                this.onFinish();
                clearInterval(this.timer);
              }
            });
          }, 1000);
        } else {
          Setting.showMessage("error", res.msg);
        }
      });

      break;
    default:
      break;
    }
  }

  onFinish() {
    const linkInStorage = sessionStorage.getItem("signinUrl");
    if (linkInStorage !== null && linkInStorage !== "") {
      Setting.goToLinkSoft(this, linkInStorage);
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  onFinishFailed(values, errorFields) {}

  renderOptions() {
    const options = [];

    if (this.state.phone !== "") {
      options.push(
        <Option key={"phone"} value={this.state.phone} >
          &nbsp;&nbsp;{this.state.phone}
        </Option>
      );
    }

    if (this.state.email !== "") {
      options.push(
        <Option key={"email"} value={this.state.email} >
          &nbsp;&nbsp;{this.state.email}
        </Option>
      );
    }

    return options;
  }

  renderForm(application) {
    const spanStyle = {
      fontWeight: 400,
      fontSize: "16px",
      color: "rgba(0,0,0,0.88)",
      lineHeight: "24px",
      marginBottom: "8px",
    };
    const spanMsg = i18next.t("forget:The login page is automatically displayed after", {time: "<span style='color:#FF921C'>" + this.state.time + "s</span>", interpolation: {escapeValue: false}});
    return (
      <Form.Provider onFormFinish={(name, {info, forms}) => {
        this.onFormFinish(name, info, forms);
      }}>
        {/* STEP 1: input username -> get email & phone */}
        {this.state.current === 0 ?
          <Form
            ref={this.form}
            name="step1"
            // eslint-disable-next-line no-console
            onFinishFailed={(errorInfo) => console.log(errorInfo)}
            initialValues={{
              application: application.name,
              organization: application.organization,
              username: this.state.email,
            }}
            style={{width: "100%"}}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your application!"),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your organization!"),
                },
              ]}
            />
            <div style={{...spanStyle}}>{i18next.t("forget:Account")}</div>
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: i18next.t("forget:Please input your email!"),
                  whitespace: true,
                },
                {
                  type: "email",
                  message: i18next.t("forget:Please input a valid email!"),
                },
              ]}
            >
              <Input
                placeholder={i18next.t("forget:Please input your email!")}
              />
            </Form.Item>
            <br />
            <Form.Item>
              <Button block type="primary" htmlType="submit">
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
          </Form> : null}

        {/* STEP 2: verify email or phone */}
        {this.state.current === 1 ? <Form
          ref={this.form}
          name="step2"
          onFinishFailed={(errorInfo) =>
            this.onFinishFailed(
              errorInfo.values,
              errorInfo.errorFields,
              errorInfo.outOfDate
            )
          }
          // onValuesChange={(changedValues, allValues) => {
          //   const verifyType = changedValues.dest?.indexOf("@") === -1 ? "phone" : "email";
          //   this.setState({
          //     dest: changedValues.dest,
          //     verifyType: verifyType,
          //   });
          // }}
          initialValues={{
            application: application.name,
            organization: application.organization,
            dest: this.state.dest,
          }}
          style={{width: "100%"}}
          size="large"
        >
          <Form.Item
            style={{height: 0, visibility: "hidden"}}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t("application:Please input your application!"),
              },
            ]}
          />
          <Form.Item
            hidden
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t("application:Please input your organization!"),
              },
            ]}
          />
          <div style={{...spanStyle}}>{i18next.t("forget:Account")}</div>
          <Form.Item
            name="dest"
            validateFirst
            hasFeedback
          >
            {
              <Select virtual={false}
                disabled={this.state.isVerifyTypeFixed}
                style={{textAlign: "left"}}
                placeholder={i18next.t("forget:Choose email or phone")}
              >
                {
                  this.renderOptions()
                }
              </Select>
            }
          </Form.Item>
          <div style={{...spanStyle}}>{i18next.t("forget:Verification code")}</div>
          <Form.Item
            name="code"
            rules={[
              {
                required: true,
                message: i18next.t("code:Please input your verification code!"),
              },
            ]}
          >
            <SendCodeInput disabled={this.state.dest === ""}
              method={"forget"}
              onButtonClickArgs={[this.state.dest, this.state.verifyType, Setting.getApplicationName(this.getApplicationObj()), this.state.name]}
              application={application}
              showIcon={false}
            />
          </Form.Item>
          <br />
          <Form.Item>
            <Button
              block
              type="primary"
              htmlType="submit"
            >
              {i18next.t("forget:Next Step")}
            </Button>
          </Form.Item>
        </Form> : null}

        {/* STEP 3 */}
        {this.state.current === 2 ?
          <Form
            ref={this.form}
            name="step3"
            onFinishFailed={(errorInfo) =>
              this.onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            initialValues={{
              application: application.name,
              organization: application.organization,
            }}
            style={{width: "100%"}}
            size="large"
          >
            <Form.Item
              hidden
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your application!"),
                },
              ]}
            />
            <Form.Item
              hidden
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t("application:Please input your organization!"),
                },
              ]}
            />
            <div style={{...spanStyle}}>{i18next.t("forget:New Password")}</div>
            <Form.Item
              name="newPassword"
              hidden={this.state.current !== 2}
              rules={[
                {
                  required: true,
                  validateTrigger: "onChange",
                  validator: (rule, value) => {
                    const errorMsg = PasswordChecker.checkPasswordComplexity(value, application.organizationObj.passwordOptions);
                    if (errorMsg === "") {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(errorMsg);
                    }
                  },
                },
              ]}
              hasFeedback
            >
              <Input.Password
                placeholder={i18next.t("forget:Please enter the new password")}
                allowClear={false}
              />
            </Form.Item>
            <div style={{...spanStyle}}>{i18next.t("forget:Confirm New Password")}</div>
            <Form.Item
              name="confirm"
              dependencies={["newPassword"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: i18next.t("forget:Please enter the new password again"),
                },
                ({getFieldValue}) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      i18next.t("signup:Your confirmed password is inconsistent with the password!")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={i18next.t("forget:Please enter the new password again")}
                allowClear={false}
              />
            </Form.Item>
            <br />
            <Form.Item hidden={this.state.current !== 2}>
              <Button block type="primary" htmlType="submit">
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
          </Form> : null}
        {this.state.current === 3 ? <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
          <img src={require("../assets/ok.png")} alt="" style={{width: "72px", height: "72px"}} />
          <div style={{textAlign: "center", fontSize: "24px", fontWeight: "bold", lineHeight: "32px", color: "rgba(0,0,0,0.88)", marginTop: "24px"}}>
            {i18next.t("forget:Reset successfully")}
          </div>
          <div style={{textAlign: "center", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "rgba(0,0,0,0.45)", marginTop: "8px"}} dangerouslySetInnerHTML={{__html: spanMsg}}></div>

          <Button size={"large"} block type="primary" style={{marginTop: "24px"}} onClick={() => this.onFinish()}>{i18next.t("forget:Log In Now")}</Button>
        </div> : null}
      </Form.Provider>
    );
  }

  stepBack() {
    // 第二步可返回上一步
    if (this.state.current === 1) {
      this.setState({
        current: this.state.current - 1,
      });
    } else if (this.props.history.length > 1) {
      this.props.history.goBack();
    } else {
      Setting.redirectToLoginPage(this.getApplicationObj(), this.props.history);
    }
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined) {
      return null;
    }
    if (application === null) {
      return Util.renderMessageLarge(this, this.state.msg);
    }

    return (
      <React.Fragment>
        <img className="forget-page-box" src={require("../assets/forget_bg.png")} alt="" style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          opacity: "1",
        }} />
        <CustomGithubCorner />
        <div className="forget-content" style={{padding: Setting.isMobile() ? "0" : null, boxShadow: Setting.isMobile() ? "none" : null, backgroundColor: "#fff", zIndex: "1"}}>
          {this.state.current < 3 ? <Button className="forget-button" icon={<ArrowLeftOutlined />} onClick={() => {this.stepBack();}}>
            <span>{this.state.current === 0 ? i18next.t("forget:Return to login") : this.state.current === 1 ? i18next.t("forget:Last Step") : i18next.t("forget:Cancel")}</span>
          </Button> : ""}
          <Row>
            <Col span={24} style={{justifyContent: "center"}}>
              <Row>
                <Col span={24}>
                  <div style={{textAlign: "center", fontSize: "24px", fontWeight: "bold", color: "rgba(0,0,0,0.88)", marginTop: "40px"}}>
                    {
                      // Setting.renderHelmet(application)
                      Setting.renderHelmet({
                        organizationObj: {
                          displayName: i18next.t("forget:Forget Password"),
                          favicon: require("../assets/favicon.ico"),
                        },
                      })
                    }
                    {i18next.t("forget:Forget Password")}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Steps
                    className="forgetPage-customSteps"
                    responsive={false}
                    size={Setting.isMobile() ? "small" : "default"}
                    current={this.state.current}
                    labelPlacement="vertical"
                    items={[
                      {
                        title: i18next.t("forget:Account"),
                      },
                      {
                        title: i18next.t("forget:Verify"),
                      },
                      {
                        title: i18next.t("forget:Reset"),
                      },
                      {
                        title: i18next.t("forget:Complete"),
                      },
                    ]}
                    style={{
                      boxSizing: "border-box",
                      margin: "auto",
                      marginTop: "40px",
                    }}
                  >
                  </Steps>
                </Col>
              </Row>
            </Col>
            <Col span={24} style={{display: "flex", justifyContent: "center"}}>
              <div style={{marginTop: "40px", width: "100%"}}>
                {this.renderForm(application)}
              </div>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(ForgetPage);
